import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  WindIcon,
  PersonStandingIcon, HeartPulseIcon, ShieldCheckIcon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line } from 'recharts';

import { useTelemetry } from '../hooks/useTelemetry';
import { useTelemetryHistory } from '../hooks/useHistoricalTelemetry';
import { tbToChartData } from '../utils/tbChartHelper';
import { useTranslation } from 'react-i18next';

import {
  TimeRange, SensorDetailSidebarProps,
  ChartSection, SensorHeader
} from './SensorDetailShared';

// ─── RADAR Detail View ───
export function RADARDetail({ sensor, onBack, onClose, onUpdateSensor }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const { data: realtimeData } = useTelemetry(deviceId, ['hr', 'br', 'cl', 'isPresentFromDevice']);
  
  const [hrRange, setHrRange] = useState<TimeRange>('realtime');
  const [brRange, setBrRange] = useState<TimeRange>('realtime');
  const [clRange, setClRange] = useState<TimeRange>('realtime');
  
  const { data: histHr } = useTelemetryHistory(deviceId, ['hr'], hrRange);
  const { data: histBr } = useTelemetryHistory(deviceId, ['br'], brRange);
  const { data: histCl } = useTelemetryHistory(deviceId, ['cl'], clRange);

  const [isPresent, setIsPresent] = useState(false);

  // Sync UI state with ThingsBoard telemetry
  useEffect(() => {
    const presenceVal = realtimeData['isPresentFromDevice']?.[0]?.value;
    if (presenceVal !== undefined) {
      const strVal = String(presenceVal);
      setIsPresent(strVal === 'true' || strVal === '1');
    }
  }, [realtimeData]);

  const hrValue = realtimeData['hr']?.[0]?.value !== undefined ? Number(realtimeData['hr'][0].value) : 0;
  const brValue = realtimeData['br']?.[0]?.value !== undefined ? Number(realtimeData['br'][0].value) : 0;
  const clValue = realtimeData['cl']?.[0]?.value !== undefined ? Number(realtimeData['cl'][0].value) : 0;
  
  const finalHrData = useMemo(() => {
    return hrRange === 'realtime'
      ? tbToChartData(realtimeData, 'hr', 'realtime', [])
      : tbToChartData(histHr, 'hr', hrRange, []);
  }, [hrRange, realtimeData, histHr]);

  const finalBrData = useMemo(() => {
    return brRange === 'realtime'
      ? tbToChartData(realtimeData, 'br', 'realtime', [])
      : tbToChartData(histBr, 'br', brRange, []);
  }, [brRange, realtimeData, histBr]);

  const finalClData = useMemo(() => {
    return clRange === 'realtime'
      ? tbToChartData(realtimeData, 'cl', 'realtime', [])
      : tbToChartData(histCl, 'cl', clRange, []);
  }, [clRange, realtimeData, histCl]);

  const hrStatus = hrValue === 0 ? '--' : hrValue < 60 ? t('sensor.bradycardia') : hrValue > 100 ? t('sensor.tachycardia') : t('sensor.normal');
  const hrColor = hrValue === 0 ? 'text-slate-500' :
    hrValue >= 60 && hrValue <= 100 ?
    'text-emerald-400' :
    hrValue < 60 ?
    'text-cyan-400' :
    'text-red-400';
    
  const brStatus = brValue === 0 ? '--' : brValue >= 12 && brValue <= 20 ? t('sensor.normal') : t('sensor.caution');
  const brColor = brValue === 0 ? 'text-slate-500' :
    brValue >= 12 && brValue <= 20 ? 'text-emerald-400' : 'text-amber-400';
    
  const clStatus = clValue === 0 ? '--' : clValue >= 80 ? t('sensor.high') : clValue >= 50 ? t('sensor.status_fair') : t('sensor.low');
  const clColor = clValue === 0 ? 'text-slate-500' :
    clValue >= 80 ?
    'text-emerald-400' :
    clValue >= 50 ?
    'text-amber-400' :
    'text-red-400';

  return (
    <div className="w-full flex flex-col gap-2 p-3 border-l border-white/5 bg-[#0a0f1a]/30 h-full overflow-hidden">
      <SensorHeader
        sensor={sensor}
        subtitle="Radar Sleep Sensor"
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
        rightSlot={
        <div className="flex items-center gap-2">
            <div
            className={`flex flex-col items-center gap-1 p-1.5 rounded-xl ${isPresent ? 'bg-rose-500/20 text-rose-400' : 'bg-white/5 text-slate-600'}`}
            title={t('sensor.presence_status')}>
            
              <PersonStandingIcon
              className="w-7 h-7"
              fill={isPresent ? 'currentColor' : 'none'} />
            
            </div>
          </div>
        } />
      

      {/* Quick Status Overview */}
      <motion.div
        initial={{
          opacity: 0,
          y: 10
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        className="grid grid-cols-3 gap-2 flex-shrink-0">
        
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
            HR
          </div>
          <div className={`text-base font-roboto font-bold ${hrColor}`}>
            {hrValue === 0 ? '--' : hrValue}
          </div>
          <div className="text-[0.6rem] text-slate-600 font-roboto">bpm</div>
          <div className={`text-[0.7rem] font-bold ${hrColor}`}>{hrStatus}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
            BR
          </div>
          <div className={`text-base font-roboto font-bold ${brColor}`}>
            {brValue === 0 ? '--' : brValue}
          </div>
          <div className="text-[0.6rem] text-slate-600 font-roboto">rpm</div>
          <div className={`text-[0.7rem] font-bold ${brColor}`}>{brStatus}</div>
        </div>
        <div className="glass-panel rounded-xl p-2 text-center">
          <div className="text-[0.9rem] text-slate-500 uppercase tracking-wider font-bold mb-0.5">
            CL
          </div>
          <div className={`text-base font-roboto font-bold ${clColor}`}>
            {clValue === 0 ? '--' : clValue}
          </div>
          <div className="text-[0.6rem] text-slate-600 font-roboto">{t('sensor.confidence_level')}</div>
          <div className={`text-[0.7rem] font-bold ${clColor}`}>{clStatus}</div>
        </div>
      </motion.div>

      <ChartSection
        icon={HeartPulseIcon}
        iconColor="text-rose-400"
        title="Heart Rate (HR)"
        data={finalHrData}
        timeRange={hrRange}
        onTimeRangeChange={setHrRange}
        chartType="area"
        color="#fb7185"
        gradientId="radarHr"
        className="flex-1 min-h-0"
        stats={[
        {
          label: t('sensor.current'),
          value: hrValue > 0 ? `${hrValue} bpm` : '--',
          color: hrColor
        },
        {
          label: t('sensor.average'),
          value: finalHrData.length > 0 ? `${(finalHrData.reduce((sum, item) => sum + item.value, 0) / finalHrData.length).toFixed(1)} bpm` : '--'
        }]
        } />
      

      <ChartSection
        icon={WindIcon}
        iconColor="text-sky-400"
        title="Breathe Rate (BR)"
        data={finalBrData}
        timeRange={brRange}
        onTimeRangeChange={setBrRange}
        chartType="line"
        color="#38bdf8"
        gradientId="radarBr"
        className="flex-1 min-h-0"
        stats={[
        {
          label: t('sensor.current'),
          value: brValue > 0 ? `${brValue} rpm` : '--',
          color: brColor
        },
        {
          label: t('sensor.average'),
          value: finalBrData.length > 0 ? `${(finalBrData.reduce((sum, item) => sum + item.value, 0) / finalBrData.length).toFixed(1)} rpm` : '--'
        }]
        } />
      

      <ChartSection
        icon={ShieldCheckIcon}
        iconColor="text-emerald-400"
        title="Confidence Level (CL)"
        data={finalClData}
        timeRange={clRange}
        onTimeRangeChange={setClRange}
        chartType="area"
        color="#34d399"
        gradientId="radarCl"
        className="flex-1 min-h-0"
        stats={[
        {
          label: t('sensor.current'),
          value: clValue > 0 ? `${clValue}` : '--',
          color: clColor
        },
        {
          label: t('sensor.average'),
          value: finalClData.length > 0 ? `${(finalClData.reduce((sum, item) => sum + item.value, 0) / finalClData.length).toFixed(1)}` : '--'
        }]
        } />
      
    </div>);
}

